import { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react'

import { useImageCrop } from '#/components/FormCall/Inputs/InputImage/Logic/useImageCrop'
import { imageValidation } from '#/components/FormCall/Inputs/InputImage/Logic/validation'
import {
  ImageAction,
  ImageActionKey,
  InputImageState,
  UseInputImageParams,
} from '#/components/FormCall/Inputs/InputImage/types'
import { urlToFile } from '#/utils/download'

const resolveImageActions = (cropping: boolean, failed: boolean) => {
  const actions: ImageAction[] = [
    { color: 'primary', enabled: cropping && !failed, icon: 'check', key: 'save' },
    { color: 'primary', enabled: !cropping && !failed, icon: 'crop', key: 'crop' },
    { color: 'error', enabled: true, icon: 'trash', key: 'clear' },
  ]

  return actions.filter(({ enabled }) => enabled)
}

export const useInputImage = ({
  accept,
  onChange,
  output,
  value,
}: UseInputImageParams): InputImageState => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [hover, setHover] = useState(false)
  const [file, setFile] = useState<File>()
  const [preview, setPreview] = useState<File>()
  const [picked, setPicked] = useState(false)
  const [loading, setLoading] = useState(typeof value === 'string')
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (typeof value !== 'string') return

    urlToFile({ url: value }).then(loaded => {
      setLoading(false)
      setFailed(!loaded)
      loadFile(loaded)
    })
  }, [])

  const loadFile = (loaded?: File) => {
    if (!loaded) return
    setFile(loaded)
    setPreview(loaded)
  }

  const {
    aspectLocked,
    crop,
    cropping,
    endCropping,
    imageRef,
    onCropChange,
    onCropComplete,
    onImageLoad,
    saveCrop,
    startCropping,
  } = useImageCrop({ file, onChange, output, picked, setPreview })

  const uploadFile = (selected?: File) => {
    const isValid = imageValidation({ accept, file: selected })

    if (!isValid) return

    loadFile(selected)
    setPicked(true)
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    uploadFile(event.target.files?.[0])
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    uploadFile(event.dataTransfer.files?.[0])
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => event.preventDefault()

  const handleClear = () => {
    setFile(undefined)
    setPreview(undefined)
    setPicked(false)
    setFailed(false)
    endCropping()

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    onChange?.(null)
  }

  const handleAction = async (key: ImageActionKey) => {
    switch (key) {
      case 'clear': {
        handleClear()
        break
      }
      case 'crop': {
        startCropping()
        break
      }
      case 'save': {
        await saveCrop()
        endCropping()
        break
      }
    }
  }

  const handleMouseEnter = () => setHover(true)

  const handleMouseLeave = () => setHover(false)

  const openFileDialog = () => fileInputRef.current?.click()

  return {
    actions: resolveImageActions(cropping, failed),
    aspectLocked,
    crop,
    cropping,
    failed,
    file,
    fileInputRef,
    handleAction,
    handleDragOver,
    handleDrop,
    handleFileChange,
    handleMouseEnter,
    handleMouseLeave,
    hover,
    imageRef,
    loading,
    onCropChange,
    onCropComplete,
    onImageLoad,
    openFileDialog,
    preview,
  }
}
